import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { groupService, authService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Users, PlusCircle, Gift, ShieldUser } from 'lucide-react';
import { useModal } from '../context/ModalContext';

const Dashboard = () => {
  const modal = useModal();
  const { user, refreshUser } = useAuth();
  const [groupName, setGroupName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [childName, setChildName] = useState('');
  const navigate = useNavigate();

  const handleCreateChildAccount = async (e) => {
    e.preventDefault();
    try {
      await authService.createChildAccount({ name: childName });
      await refreshUser();
      setChildName('');
      modal.alert({ title: 'Success', message: 'Child account and wishlist created successfully!', variant: 'success' });
    } catch (err) {
      modal.alert({ title: 'Error', message: err.response?.data?.msg || 'Error creating child account', variant: 'danger' });
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await groupService.create({ name: groupName });
      await refreshUser();
      setGroupName('');
    } catch (err) {
      modal.alert({ title: 'Error', message: 'Error creating group', variant: 'danger' });
    }
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();
    try {
      await groupService.join({ groupCode: joinCode });
      await refreshUser();
      setJoinCode('');
    } catch (err) {
      modal.alert({ title: 'Error', message: 'Error joining group', variant: 'danger' });
    }
  };

  const handleJoinCodeChange = (e) => {
    const val = e.target.value;
    let cleaned = val.toUpperCase();
    cleaned = cleaned.replace(/[^A-Z0-9]/g, '');

    if (cleaned.length > 8) {
      cleaned = cleaned.substring(0, 8);
    }

    let formatted = cleaned;
    if (cleaned.length > 4) {
      formatted = `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    }

    setJoinCode(formatted);
  };

  return (
    <div className="max-w-6xl mx-auto mt-10 px-4">
      <h1 className="text-4xl font-bold text-xmas-red mb-8 text-center">Welcome, {user.name}!</h1>

      {user.familyGroups && user.familyGroups.length > 0 ? (
        <div className="space-y-6 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {user.familyGroups.map((group) => (
              <div key={group.id} className="p-6 rounded-xl border border-xmas-green/30 bg-xmas-green/10">
                <h3 className="text-xl font-bold text-xmas-green mb-2">
                  Family: {group.name}
                </h3>
                <p className="text-lg">
                  Group Code: <span className="font-mono font-bold text-xmas-red">{group.groupCode}</span>
                </p>
                <p className="mt-2 text-sm text-gray-600">Share this code with your family members so they can join!</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="card flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-bold text-xmas-red mb-2 flex items-center gap-2">
                  <Gift /> My Wishlist
                </h3>
                <p className="text-gray-600">Add items you'd love to receive this year.</p>
              </div>
              <button onClick={() => navigate('/my-wishlist')} className="btn-primary w-full mt-4">
                Manage
              </button>
            </div>
            <div className="card flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-bold text-xmas-green mb-2 flex items-center gap-2">
                  <Users /> Family Wishlists
                </h3>
                <p className="text-gray-600">Check out what your family wants and mark items as bought.</p>
              </div>
              <button onClick={() => navigate('/family-wishlists')} className="btn-secondary w-full mt-4">
                See All
              </button>
            </div>
          </div>

          {user.children && user.children.length > 0 && (
            <div className="mt-8">
              <h3 className="text-2xl font-bold text-xmas-red mb-4">Children's Wishlists</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {user.children.map((child) => (
                  <div key={child.id} className="card flex flex-col justify-between">
                    <div>
                      <h4 className="text-lg font-bold text-xmas-green flex items-center gap-2 mb-2">
                        <ShieldUser size={20} /> {child.name}'s Wishlist
                      </h4>
                      <p className="text-sm text-gray-600">Manage items and share settings for {child.name}.</p>
                    </div>
                    <button
                      onClick={() => navigate(`/my-wishlist?childId=${child.id}`)}
                      className="btn-primary w-full mt-4"
                    >
                      Manage {child.name}'s Wishlist
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card text-center py-8 mb-10 bg-gray-50">
          <p className="text-lg text-gray-600 italic">You are not part of any family groups yet. Create or join one below!</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
        <div className="card">
          <h2 className="text-2xl font-bold text-xmas-red mb-4 flex items-center gap-2">
            <PlusCircle /> Create a Family Group
          </h2>
          <form onSubmit={handleCreateGroup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Family Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded mt-1"
                placeholder="The Stephensons"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-primary w-full">Create</button>
          </form>
        </div>

        <div className="card">
          <h2 className="text-2xl font-bold text-xmas-green mb-4 flex items-center gap-2">
            <Users /> Join a Family Group
          </h2>
          <form onSubmit={handleJoinGroup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Group Code</label>
              <input
                type="text"
                className="w-full p-2 border rounded mt-1 text-center tracking-wider uppercase"
                placeholder="e.g., ABCD-1234"
                value={joinCode}
                onChange={handleJoinCodeChange}
                maxLength={9}
                required
              />
            </div>
            <button type="submit" className="btn-secondary w-full">Join Group</button>
          </form>
        </div>

        <div className="card">
          <h2 className="text-2xl font-bold text-xmas-red mb-4 flex items-center gap-2">
            <ShieldUser /> Create a Child List
          </h2>
          <form onSubmit={handleCreateChildAccount} className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Child's First Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded mt-1"
                placeholder="Bobby"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-primary w-full">Create Child List</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
